import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import {
	type PrefetchedConsentPreferenceData,
	shouldRenderBannerPrefetched,
} from '@atlassian/browser-storage-controls';

import { expVal } from '@confluence/feature-experiments';

import { ssrBannerManager } from '@confluence/ssr-utilities/entry-points/SSRBannerManager.ts';
import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';

import { MIN_BANNER_HEIGHT, BANNER_NAME } from './constants.ts';

declare global {
	interface Window {
		__COOKIE_CONSENT_PREFERENCES__?: PrefetchedConsentPreferenceData | null;
	}
}

export function useCookiesConsentBanner(
	bannerState?: BannerStateContainer | undefined,
	bannerRef?: React.RefObject<HTMLDivElement | null>,
) {
	const cookiesDialogRef = useRef<HTMLDivElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [height, setHeight] = useState(MIN_BANNER_HEIGHT);
	const prefetchedData = Object.keys(window?.__COOKIE_CONSENT_PREFERENCES__ || {}).length
		? window.__COOKIE_CONSENT_PREFERENCES__
		: undefined;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const shouldRenderInitialDeps = [prefetchedData, isSSR];

	// Use useMemo to calculate shouldRenderInitial only once, regardless of SSR or client
	const shouldRenderInitial = useMemo(() => {
		// If there is no data, it may be because there is not a userId. Defer to package internal logic to determine whether to render
		const initialValue = shouldRenderBannerPrefetched(prefetchedData ?? undefined);

		// Only register banner during SSR
		if (isSSR && initialValue) {
			ssrBannerManager.registerBanner({
				name: BANNER_NAME,
				height: MIN_BANNER_HEIGHT,
			});
		}

		return initialValue;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, shouldRenderInitialDeps);

	const [shouldRender, setShouldRender] = useState<boolean | undefined>(
		isSSR || expVal('confluence_cookie_banner_hydration_fix', 'isEnabled', false)
			? shouldRenderInitial
			: undefined,
	);

	if (shouldRender) {
		bannerState?.show(BANNER_NAME, height);
	} else if (shouldRender === false) {
		bannerState?.hide(BANNER_NAME);
	}

	const findCookiesDialog = useCallback(() => {
		if (cookiesDialogRef?.current) {
			return cookiesDialogRef?.current;
		}

		if (!bannerRef?.current) {
			return null;
		}
		const childDivs = bannerRef?.current.getElementsByTagName('div');
		let cookiesDialog = null;
		for (const element of Object.values(childDivs)) {
			if (!element.offsetHeight) {
				continue;
			}
			// @ts-ignore - Type 'HTMLDivElement' is not assignable to type 'null'
			// Using `ts-ignore` and not `ts-expect-error` here as TypeScript will fail on this line without `ts-ignore` but with `ts-expect-error`
			// This error was introduced after upgrading to TypeScript 5
			cookiesDialog = element;
			break;
		}
		cookiesDialogRef.current = cookiesDialog;
		return cookiesDialog;
	}, [bannerRef]);

	useEffect(() => {
		if (shouldRender || shouldRenderInitial) {
			addUFOCustomData({
				isCookieConsentBannerDisplayed: true,
				...(prefetchedData && {
					prefetchedConsentPreferences: JSON.stringify(prefetchedData),
				}),
			});
		}
	}, [shouldRender, shouldRenderInitial, prefetchedData]);

	useEffect(() => {
		if (shouldRender) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				action: 'rendered',
				actionSubject: 'cookiesConsentBanner',
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldRender]);

	const onWidthChange = useCallback(() => {
		const cookiesDialog = findCookiesDialog();
		if (!cookiesDialog) {
			return;
		}
		addUFOCustomData({
			cookieBannerHeight: cookiesDialog.offsetHeight,
		});
		setHeight(cookiesDialog.offsetHeight);
	}, [findCookiesDialog]);

	const showBanner = useCallback(() => {
		setShouldRender(true);
		onWidthChange();
	}, [onWidthChange]);

	const hideBanner = useCallback(() => {
		setShouldRender(false);
	}, []);

	return {
		shouldRender,
		showBanner,
		hideBanner,
		onWidthChange,
		prefetchedData,
	};
}
